import React, { useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import useUploadMedia from "../../../hooks/file/useUploadMedia";
import toast from "react-hot-toast";
import Spinner from "../../../components/spinner";

const ProductDetails = ({ productData, setProductData }) => {
  // Add local loadingIndex state
  const [loadingIndex, setLoadingIndex] = useState(null);

  // Use upload media hook
  const { upload } = useUploadMedia({
    url: "upload/image",
    onSuccess: (res) => {
      const { image_url, image_id } = res?.data;
      // The index will be captured in the closure
      if (upload.currentIndex === undefined) return;

      const tempArr = [...productData.product_details];
      tempArr[upload.currentIndex] = {
        ...tempArr[upload.currentIndex],
        image_id: image_id,
        image_url: image_url,
      };

      setProductData({
        ...productData,
        product_details: tempArr,
      });

      toast.success("Image uploaded successfully", {
        position: "top-right",
        duration: 3000,
      });
      // Reset after use
      upload.currentIndex = undefined;
      setLoadingIndex(null); // Reset loading index
    },
    onError: (err) => {
      const apiMessage = err?.response?.data?.message;
      toast.error(apiMessage || "Error uploading image", {
        position: "top-right",
        duration: 3000,
      });
      upload.currentIndex = undefined;
      setLoadingIndex(null); // Reset loading index
    },
  });

  const handleAddProductDetail = () => {
    const tempArr = productData.product_details;
    tempArr.push({
      title: "",
      content: [""],
      image_id: "",
      image_url: "",
    });
    setProductData({
      ...productData,
      product_details: tempArr,
    });
  };

  const handleAddProductDescription = (idx) => {
    const tempArr = productData.product_details;
    tempArr[idx].content.push("");
    setProductData({
      ...productData,
      product_details: tempArr,
    });
  };

  const handleProductDetailTitleChange = (e, idx) => {
    const val = e.target.value;
    const tempArr = productData.product_details;
    tempArr[idx].title = val;
    setProductData({
      ...productData,
      product_details: tempArr,
    });
  };

  const handleProductDetailDescriptionChange = (e, idx, cIdx) => {
    const val = e.target.value;
    const tempArr = productData.product_details;
    tempArr[idx].content[cIdx] = val;
    setProductData({
      ...productData,
      product_details: tempArr,
    });
  };

  const handleDeleteProductDetail = (idx) => {
    const tempArr = productData.product_details;
    tempArr.splice(idx, 1);
    setProductData({
      ...productData,
      product_details: tempArr,
    });
  };

  const handleDeleteProductDetailDescription = (idx, cIdx) => {
    const tempArr = productData.product_details[idx].content;
    tempArr.splice(cIdx, 1);
    if (tempArr?.length < 1) {
      handleDeleteProductDetail(idx);
    }
    const tempProductDetails = productData.product_details;
    setProductData({
      ...productData,
      product_details: tempProductDetails,
    });
  };

  // Handle image upload for a specific product detail using closure
  const handleUploadImage = (idx) => (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = {
      screen: "products",
      thumbnail: [file],
    };

    upload.currentIndex = idx;
    setLoadingIndex(idx); // Set loading index to current idx
    upload.mutate(data);
  };

  // Function to open file dialog for a specific product detail
  const handleOpenFileDialog = (idx) => {
    const fileInput = document.getElementById(`product-detail-image-${idx}`);
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <div>
      <div className="my-7 flex items-center justify-between border-b border-gray-400">
        <h1 className="font-semibold text-2xl pb-3">Product Details</h1>
        <div>
          <button
            onClick={handleAddProductDetail}
            type="button"
            className="border-gray-800 bg-gray-800 text-white px-6 py-2 rounded mb-2"
          >
            <span>Add Detail</span>
          </button>
        </div>
      </div>
      <div>
        {productData?.product_details?.map((item, idx) => {
          return (
            <div
              key={idx}
              className="border rounded p-4 border-gray-500 shadow-lg mt-4"
            >
              <div className="flex justify-between">
                <div className="w-2/3">
                  <div className="flex items-center justify-between">
                    <label className="label">Title</label>
                    <div
                      className="px-2 h-full flex items-center justify-center"
                      onClick={() => handleDeleteProductDetail(idx)}
                    >
                      <IconButton
                        color="error"
                        aria-label="add an delete"
                        size="large"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Detail Title"
                    name="title"
                    className="input"
                    value={productData?.product_details[idx]?.title}
                    onChange={(e) => handleProductDetailTitleChange(e, idx)}
                  />

                  <div className="mt-4">
                    <label className="label">Description</label>
                    {item?.content.map((it, cIdx) => {
                      return (
                        <div key={cIdx} className="flex items-center mb-2">
                          <input
                            key={cIdx}
                            type="text"
                            placeholder="Description"
                            name="title"
                            className="input"
                            value={
                              productData?.product_details[idx]?.content[cIdx]
                            }
                            onChange={(e) =>
                              handleProductDetailDescriptionChange(e, idx, cIdx)
                            }
                          />
                          <div
                            className="px-2 h-full flex items-center justify-center"
                            onClick={() =>
                              handleDeleteProductDetailDescription(idx, cIdx)
                            }
                          >
                            <IconButton
                              color="error"
                              aria-label="add an delete"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handleAddProductDescription(idx)}
                    type="button"
                    className="border-gray-800 bg-gray-800 text-white px-6 py-2 rounded mb-2 w-full mt-2"
                  >
                    <span>Add Description</span>
                  </button>
                </div>

                {/* Image Upload Section */}
                <div className="w-1/3 pl-4 border-l">
                  <label className="label">Image</label>
                  <div className="flex flex-col items-center">
                    {loadingIndex === idx ? (
                      <Spinner />
                    ) : (
                      <>
                        {item.image_url ? (
                          <div className="mb-3">
                            <img
                              src={item.image_url}
                              alt={`Product detail ${idx + 1}`}
                              className="object-contain w-32 h-32 border p-2"
                            />
                          </div>
                        ) : (
                          <div className="mb-3 border-dashed border-2 border-gray-300 w-32 h-32 flex items-center justify-center">
                            <span className="text-gray-400 text-sm text-center">
                              No image
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className="mt-2"
                      onClick={() => handleOpenFileDialog(idx)}
                    >
                      {item.image_url ? "Change Image" : "Upload Image"}
                    </Button>

                    <input
                      id={`product-detail-image-${idx}`}
                      type="file"
                      className="hidden"
                      onChange={handleUploadImage(idx)}
                      accept="image/jpeg, image/png, image/jpg, image/webp"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductDetails;
